import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue";
const _hoisted_1 = { class: "space-y-4" };
const _hoisted_2 = {
    key: 1,
    class: "py-6 text-center text-sm text-gray-600"
};
const _hoisted_3 = { class: "space-y-4" };
const _hoisted_4 = ["disabled"];
const _hoisted_5 = { key: 0 };
const _hoisted_6 = { key: 1 };
import { inject, onMounted, ref } from 'vue';
import { computed } from 'vue';
// `/pure` lädt das Stripe-Script NICHT beim Import (sonst zieht @stripe/stripe-js js.stripe.com
// als Seiteneffekt auf JEDER Seite nach -> render-blocking auf der Startseite). Erst loadStripe()
// im Checkout lädt das Script. Typen per `import type` (zur Laufzeit entfernt, kein Seiteneffekt).
import { loadStripe } from '@stripe/stripe-js/pure';
import { useTheme } from '@shared/composables/useTheme';
import { CHECKOUT_TURNSTILE_KEY } from '@shared/composables/useCheckoutTurnstile';
export default /*@__PURE__*/ _defineComponent({
    __name: 'StripeCheckout',
    props: {
        orderData: {},
        cartItems: {},
        deliveryType: {},
        customerInfo: {},
        deliveryAddress: {},
        specialInstructions: {}
    },
    emits: ["payment-success", "payment-error"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const apiClient = inject('apiClient');
        const checkoutTurnstile = inject(CHECKOUT_TURNSTILE_KEY);
        if (!apiClient) {
            throw new Error('RestaurantApiClient not provided');
        }
        const paymentElementContainer = ref(null);
        const isInitializing = ref(true);
        const isSubmitting = ref(false);
        const errorMessage = ref('');
        let stripe = null;
        let elements = null;
        const internalOrderNumber = ref('');
        const internalOrderId = ref('');
        const { theme } = useTheme();
        const stripeErrorStyle = computed(() => ({
            borderColor: `${(theme.value.colors.danger || theme.value.colors.primary)}55`,
            backgroundColor: `${(theme.value.colors.danger || theme.value.colors.primary)}12`,
            color: theme.value.colors.danger || theme.value.colors.primary
        }));
        const stripeButtonStyle = computed(() => ({
            backgroundColor: theme.value.colors.primary
        }));
        const initializeStripe = async () => {
            isInitializing.value = true;
            errorMessage.value = '';
            try {
                const turnstileToken = checkoutTurnstile?.getToken() || '';
                if (checkoutTurnstile?.isEnabled.value && !turnstileToken) {
                    throw new Error('Bitte die Sicherheitsprüfung bestätigen.');
                }
                const response = await apiClient.createStripePaymentIntentWithCart({
                    customer: {
                        name: props.customerInfo?.name || '',
                        email: props.customerInfo?.email || '',
                        phone: props.customerInfo?.phone || '',
                        address: props.deliveryAddress || {}
                    },
                    items: (props.cartItems || []).map((item) => ({
                        productId: item.id || item.menuItem?.id || item.menuItem?._id,
                        quantity: item.quantity || 1,
                        extraIngredientIds: (item.extras || []).map((extra) => extra.id || extra._id),
                        removedIngredientIds: item.removedIngredients || [],
                        selectedSizeId: item.selectedSize?.id,
                        specialInstructions: item.specialInstructions || ''
                    })),
                    orderType: (props.deliveryType || 'pickup'),
                    deliveryInfo: props.deliveryType === 'delivery' ? { deliveryAddress: props.deliveryAddress } : {},
                    payment: { method: 'stripe' },
                    turnstileToken: turnstileToken || undefined
                });
                const payload = response?.data || {};
                if (!payload.clientSecret || !payload.publishableKey) {
                    throw new Error('Stripe-Initialisierung fehlgeschlagen (fehlende Schluessel)');
                }
                internalOrderNumber.value = payload.orderNumber || '';
                internalOrderId.value = payload.internalOrderId || '';
                stripe = await loadStripe(payload.publishableKey);
                if (!stripe) {
                    throw new Error('Stripe konnte nicht geladen werden');
                }
                elements = stripe.elements({
                    clientSecret: payload.clientSecret,
                    appearance: { theme: 'stripe' }
                });
                const paymentElement = elements.create('payment');
                if (paymentElementContainer.value) {
                    paymentElement.mount(paymentElementContainer.value);
                }
            }
            catch (error) {
                errorMessage.value = error.message || 'Stripe konnte nicht initialisiert werden';
                emit('payment-error', errorMessage.value);
            }
            finally {
                isInitializing.value = false;
            }
        };
        const confirmPayment = async () => {
            if (!stripe || !elements) {
                errorMessage.value = 'Stripe ist nicht bereit';
                return;
            }
            isSubmitting.value = true;
            errorMessage.value = '';
            try {
                const result = await stripe.confirmPayment({
                    elements,
                    redirect: 'if_required'
                });
                if (result.error) {
                    throw new Error(result.error.message || 'Stripe-Zahlung fehlgeschlagen');
                }
                emit('payment-success', {
                    provider: 'stripe',
                    paymentIntentId: result.paymentIntent?.id,
                    orderNumber: internalOrderNumber.value,
                    internalOrderId: internalOrderId.value
                });
            }
            catch (error) {
                errorMessage.value = error.message || 'Stripe-Zahlung fehlgeschlagen';
                emit('payment-error', errorMessage.value);
            }
            finally {
                isSubmitting.value = false;
            }
        };
        onMounted(async () => {
            await initializeStripe();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (errorMessage.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "rounded-lg border p-3 text-sm",
                        style: _normalizeStyle(stripeErrorStyle.value)
                    }, _toDisplayString(errorMessage.value), 5 /* TEXT, STYLE */))
                    : _createCommentVNode("v-if", true),
                (isInitializing.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
                            _createElementVNode("i", { class: "fas fa-spinner fa-spin mr-2" }, null, -1 /* CACHED */),
                            _createTextVNode(" Stripe wird vorbereitet... ", -1 /* CACHED */)
                        ]))]))
                    : _createCommentVNode("v-if", true),
                _withDirectives(_createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", {
                        ref_key: "paymentElementContainer",
                        ref: paymentElementContainer,
                        class: "rounded-lg border border-gray-200 p-3 min-h-[90px]"
                    }, null, 512 /* NEED_PATCH */),
                    _createElementVNode("button", {
                        type: "button",
                        class: "w-full rounded-lg px-4 py-3 font-semibold text-white transition disabled:cursor-not-allowed disabled:bg-gray-400",
                        style: _normalizeStyle(stripeButtonStyle.value),
                        disabled: isSubmitting.value || isInitializing.value,
                        onClick: confirmPayment
                    }, [
                        (isSubmitting.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_5, [...(_cache[1] || (_cache[1] = [
                                    _createElementVNode("i", { class: "fas fa-spinner fa-spin mr-2" }, null, -1 /* CACHED */),
                                    _createTextVNode("Zahlung wird verarbeitet...", -1 /* CACHED */)
                                ]))]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_6, "Jetzt online bezahlen"))
                    ], 12 /* STYLE, PROPS */, _hoisted_4)
                ], 512 /* NEED_PATCH */), [
                    [_vShow, !isInitializing.value]
                ])
            ]));
        };
    }
});
